import { useCallback, useEffect, useRef, useState, type SetStateAction } from 'react'
import { ApiRequestError } from './api'
import { useViewActive } from './viewActivity'

export function useAsync<T>(load: () => Promise<T>, deps: unknown[] = [], options: { enabled?: boolean; pollMs?: number } = {}) {
  const { pollMs } = options
  const active = useViewActive()
  const enabled = (options.enabled ?? true) && active
  const [data, setState] = useState<T | null>(null)
  const [loading, setLoading] = useState(enabled)
  const [error, setError] = useState<string | null>(null)
  const runId = useRef(0)
  const inFlight = useRef<{ id: number; promise: Promise<T | null> } | null>(null)
  const refreshRef = useRef<() => Promise<T | null>>(() => Promise.resolve(null))
  const loader = useCallback(load, deps)
  // Mutation callbacks can retain refresh() from an earlier selection. Always
  // read the current selection, even when that callback finishes much later.
  const loaderRef = useRef(loader)
  loaderRef.current = loader
  const refresh = useCallback(() => {
    // Every refresh gets its own request token. A poll can already be in
    // flight when a user saves a change; reusing that promise lets the older
    // response overwrite the freshly saved state. Older responses are now
    // ignored whenever a newer refresh has started.
    const current = ++runId.current
    let settled = false
    const promise = (async () => {
    setLoading(true); setError(null)
    try {
      const value = await loaderRef.current()
      if (current === runId.current) setState(value)
      return value
    } catch (err) {
      if (current === runId.current) setError(err instanceof ApiRequestError ? err.message : err instanceof Error ? err.message : 'Request failed.')
      return null
    } finally {
      settled = true
      if (current === runId.current) setLoading(false)
      if (inFlight.current?.id === current) inFlight.current = null
    }
    })()
    if (!settled) inFlight.current = { id: current, promise }
    return promise
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loader])
  // A mutation can finish after this hook has rendered a new selection. Keep
  // the retry pointed at the latest loader instead of the stale render that
  // created the mutation callback.
  refreshRef.current = refresh
  // Polls and window focus only need the newest read. They join a request
  // that is still current instead of sending another (focus and
  // visibilitychange arrive together when a window is restored), while an
  // explicit refresh() after a save always starts a fresh read.
  const refreshInBackground = useCallback(() => {
    const pending = inFlight.current
    return pending && pending.id === runId.current ? pending.promise : refresh()
  }, [refresh])
  // A mutation that stores the server's answer directly is newer than any
  // read still in flight, so it invalidates that read just as a refresh does.
  // The invalidated read is repeated rather than dropped: it may belong to a
  // selection that changed while the save was pending.
  const setData = useCallback((value: SetStateAction<T | null>) => {
    // A save may resolve after the next selection has already finished loading.
    // Its retained setter belongs to the previous loader; do not replace the
    // new selection with that save result, even when no read is in flight.
    if (loader !== loaderRef.current) {
      void refreshRef.current()
      return
    }
    const reading = inFlight.current?.id === runId.current
    runId.current += 1
    setLoading(false)
    setState(value)
    if (reading) void refreshRef.current()
  }, [loader])
  useEffect(() => { if (enabled) void refresh() }, [enabled, refresh])
  useEffect(() => {
    if (enabled) return
    // A hidden/disabled view must invalidate a request that is still in
    // flight. Otherwise its late response can repopulate state after the
    // view has moved on, and loading would remain stuck until reactivation.
    runId.current += 1
    setLoading(false)
  }, [enabled])
  useEffect(() => {
    if (!enabled || !pollMs) return
    let stopped = false
    let timer: number
    const poll = async () => {
      if (document.visibilityState !== 'hidden') await refreshInBackground()
      if (!stopped) timer = window.setTimeout(poll, pollMs)
    }
    const resume = () => { if (document.visibilityState !== 'hidden') void refreshInBackground() }
    window.addEventListener('focus', resume)
    document.addEventListener('visibilitychange', resume)
    timer = window.setTimeout(poll, pollMs)
    return () => {
      stopped = true
      window.clearTimeout(timer)
      window.removeEventListener('focus', resume)
      document.removeEventListener('visibilitychange', resume)
    }
  }, [enabled, pollMs, refreshInBackground])
  useEffect(() => () => { runId.current += 1 }, [])
  return { data, setData, loading, error, refresh }
}

export function useDebouncedValue(value: string, delay = 260) {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => { const timer = window.setTimeout(() => setDebounced(value), delay); return () => window.clearTimeout(timer) }, [value, delay])
  return debounced
}
